<script setup lang="ts">
import { computed, ref } from 'vue';
import AllergenToggleGrid from './AllergenToggleGrid.vue';
import type { IDeclarationLine } from './regulatory-declarations.types';
import './epp-shared.css';

const props = defineProps<{
  label: string;
  modelValue: IDeclarationLine[];
}>();

const emit = defineEmits<{
  'update:modelValue': [lines: IDeclarationLine[]];
}>();

const expanded = ref(false);

const summary = computed(() => {
  const count = props.modelValue.length;
  if (count === 0) return 'Aucun allergène déclaré';
  return `${count} allergène${count > 1 ? 's' : ''} déclaré${count > 1 ? 's' : ''}`;
});

function onGridUpdate(lines: IDeclarationLine[]) {
  emit('update:modelValue', lines);
}
</script>

<template>
  <div class="aer__row" :class="{ 'aer__row--open': expanded }">
    <button
      type="button"
      class="aer__hd"
      :aria-expanded="expanded"
      @click="expanded = !expanded"
    >
      <span class="aer__label">{{ label }}</span>
      <span
        class="aer__chip"
        :class="{ 'aer__chip--filled': modelValue.length }"
        >{{ summary }}</span
      >
      <i
        class="pi pi-chevron-down aer__chevron"
        :class="{ 'aer__chevron--open': expanded }"
      />
    </button>
    <div v-if="expanded" class="aer__body">
      <AllergenToggleGrid
        compact
        :model-value="modelValue"
        @update:model-value="onGridUpdate"
      />
    </div>
  </div>
</template>

<style scoped>
.aer__row {
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.75rem;
  background: var(--nxt-surface-low);
  overflow: hidden;
}
.aer__hd {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0.875rem;
  background: transparent;
  border: none;
  cursor: pointer;
  font: inherit;
  text-align: left;
}
.aer__label {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  flex: 1;
  min-width: 0;
}
.aer__chip {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--nxt-on-surface-muted);
  background: var(--nxt-surface-mid);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 9999px;
  padding: 0.2rem 0.625rem;
  white-space: nowrap;
  flex-shrink: 0;
}
.aer__chip--filled {
  color: #ef4444;
  border-color: rgba(239, 68, 68, 0.3);
  background: rgba(239, 68, 68, 0.08);
}
.aer__chevron {
  flex-shrink: 0;
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
  transition: transform 0.2s ease;
}
.aer__chevron--open {
  transform: rotate(180deg);
}
.aer__body {
  padding: 0 0.875rem 0.875rem;
}

[data-theme='dark'] .aer__row {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .aer__chip {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .aer__chip--filled {
  background: rgba(239, 68, 68, 0.15);
  border-color: rgba(239, 68, 68, 0.35);
  color: #ff8080;
}
</style>
